"use client";

import { Button } from "@ocr-monorepo/ui/components/button";
import { toast } from "sonner";
import { signIn } from "@/lib/auth-client";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
} from "@ocr-monorepo/ui/components/form";
import { Input } from "@ocr-monorepo/ui/components/input";
import { Card } from "@ocr-monorepo/ui/components/card";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Link from "next/link";
import { env } from "@/env";
import { IconBrandGithub, IconBrandGoogle } from "@tabler/icons-react";

const authSchema = z.object({
  email: z.email(),
  password: z.string().min(8),
});
export default function LoginPage() {
  const [loading, setLoading] = useState<boolean>(false);
  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(authSchema),
  });

  const onSubmit = async (
    data: z.infer<typeof authSchema>,
    e?: React.BaseSyntheticEvent,
  ) => {
    e?.preventDefault();
    setLoading(true);
    await signIn.email(
      { email: data.email, password: data.password, callbackURL: "/dashboard" },
      {
        onSuccess() {
          setLoading(false);
        },
        onError() {
          setLoading(false);
          toast.error("Invalid email or password", {});
        },
      },
    );
  };

  return (
    <main className="grid min-h-svh bg-card text-foreground lg:grid-cols-[minmax(520px,620px)_1fr]">
      <aside className="relative hidden min-h-svh overflow-hidden border-r bg-secondary px-14 py-12 lg:flex lg:flex-col">
        <Link
          href="/"
          className="w-fit text-[26px] font-semibold leading-8 tracking-[-0.06em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          pdfparse
        </Link>

        <div className="mt-[126px] max-w-[500px]">
          <h2 className="max-w-[490px] text-5xl font-medium leading-[54px] tracking-[-0.04em]">
            Less paperwork.
            <br />
            More possibility.
          </h2>
          <p className="mt-6 max-w-[450px] text-[17px] leading-7 text-muted-foreground">
            Turn your PDFs into organized, usable data. Start with a few
            documents and build from there.
          </p>
        </div>

        <Card className="mt-20 gap-0 overflow-hidden py-0">
          <div className="flex h-[52px] items-center justify-between border-b px-[18px]">
            <span className="text-sm">Invoices</span>
            <span className="text-xs text-muted-foreground">
              3 documents · Table view
            </span>
          </div>
          <div className="grid grid-cols-[1.4fr_0.7fr_1fr] bg-background px-4 py-2 text-[11px] font-medium text-muted-foreground">
            <span>Document</span>
            <span>Invoice #</span>
            <span className="text-right">Total</span>
          </div>
          {[
            ["harbour-office.pdf", "HO-1048", "$1,495.00"],
            ["island-supply.pdf", "IS-7782", "$874.00"],
            ["kingston-printing.pdf", "KP-1092", "$557.75"],
          ].map(([document, invoice, total]) => (
            <div
              key={document}
              className="grid h-9 grid-cols-[1.4fr_0.7fr_1fr] items-center border-t px-4 text-xs"
            >
              <span className="truncate">{document}</span>
              <span className="text-muted-foreground">{invoice}</span>
              <span className="text-right tabular-nums">{total}</span>
            </div>
          ))}
        </Card>

        <p className="mt-auto text-[13px] leading-[21px] text-muted-foreground">
          Define your fields. Upload your PDFs.
          <br />
          Let PdfParse take care of the filing.
        </p>
      </aside>

      <section className="flex min-h-svh items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-[420px]">
          <Link
            href="/"
            className="mb-16 block w-fit text-2xl font-semibold tracking-[-0.06em] lg:hidden"
          >
            pdfparse
          </Link>
          <div className="flex flex-col gap-5">
            <div className="mb-3 flex flex-col items-start gap-3">
              <h1 className="text-[34px] font-medium leading-10 tracking-[-0.02em]">
                Welcome back
              </h1>
              <p className="text-[15px] leading-6 text-muted-foreground">
                Sign in to continue working with your documents.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                type="button"
                variant="outline"
                size="auth"
                className="w-full bg-card"
                disabled={loading}
                onClick={() =>
                  signIn.social({
                    provider: "google",
                    callbackURL: `${env.NEXT_PUBLIC_BASE_URL}/getting-started`,
                  })
                }
              >
                <IconBrandGoogle className="size-4" />
                Continue with Google
              </Button>
              <Button
                type="button"
                variant="outline"
                size="auth"
                className="w-full bg-card"
                disabled={loading}
                onClick={() =>
                  signIn.social({
                    provider: "github",
                    callbackURL: `${env.NEXT_PUBLIC_BASE_URL}/getting-started`,
                  })
                }
              >
                <IconBrandGithub className="size-4" />
                Continue with GitHub
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2">or use your email</span>
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-4">
                    <FormField
                      name="email"
                      render={({ field }) => {
                        return (
                          <FormItem className="grid gap-2">
                            <FormLabel htmlFor="email">Email</FormLabel>
                            <Input
                              {...field}
                              id="email"
                              type="email"
                              autoComplete="username"
                              className="h-[46px] bg-card"
                              placeholder="you@example.com"
                              required
                            />
                          </FormItem>
                        );
                      }}
                    />
                    <FormField
                      name="password"
                      render={({ field }) => {
                        return (
                          <FormItem className="grid gap-2">
                            <FormLabel htmlFor="password">Password</FormLabel>
                            <Input
                              {...field}
                              id="password"
                              type="password"
                              autoComplete="current-password"
                              className="h-[46px] bg-card"
                              placeholder="••••••••"
                              required
                            />
                          </FormItem>
                        );
                      }}
                    ></FormField>
                  </div>
                  <Button
                    type="submit"
                    size="auth"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        {" "}
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Logging you in...
                      </>
                    ) : (
                      <>Sign in</>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
            <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
              By clicking continue, you agree to our{" "}
              <Link href="/terms-and-conditions">Terms of Service</Link> and{" "}
              <Link href="/privacy">Privacy Policy</Link>.
            </div>
            <p className="text-center text-[13px] text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link
                href="/getting-started/create-account"
                className="font-medium text-primary hover:underline"
                prefetch
              >
                Create one
              </Link>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
